/**
 * SoundExpertNode (Canvas V2)
 *
 * Redesigned audio generation node matching the borderless card pattern
 * used by VideoGeneratorNode and ImageGeneratorNode.
 *
 * Features:
 * - FloatingNodeLabel (editable name above card)
 * - Borderless card with selection ring via boxShadow
 * - Waveform display (40 bars, 100px height) with click-to-play
 * - Quick Info Bar: Config | Variants (2-column grid)
 * - UnifiedBottomPanel with Config + Variants tabs
 * - Config tab: ElevenLabs-style compact toolbar with flyover popovers
 * - Transparent handles (VideoGeneratorNode pattern)
 * - Toolbar: Generate, Download, Duplicate, Delete
 */

import { memo, useState, useCallback, useEffect, useMemo } from 'react'
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow'
import { SlidersHorizontal, LayoutGrid, MessageSquarePlus, BookmarkPlus } from 'lucide-react'
import { createDebug, getAssetUrl, executeNode, InsufficientSparksError, getTypedNodeData, type SoundExpertNodeData } from '@ponpon/api-client'
import { useCanvasOptional } from '../../CanvasContext'
import type { ReactFlowCanvasEdgeData, ReactFlowNodeData } from '../../../../hooks/useYCanvas'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  useNodeActions,
  useBottomPanelAutoOpen,
  UnifiedBottomPanel,
  type ToolbarAction,
  type TabDefinition,
} from '../shared'
import { SoundExpertConfigTab, SOUND_ACCENT, type SoundMode } from '../shared/SoundExpertConfigTab'
import type { RichTextBoxState } from '../shared/RichTextBox'
import { AudioVariantsTabContent, type AudioVariant } from '../shared/AudioVariantsTabContent'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useShowInsufficientSparks } from '../../../../contexts/InsufficientSparksContext'
import { useClickLock } from '../../../../hooks/useClickLock'
import { buildAssetDownloadFilename } from '../../../../utils/buildDownloadFilename'
import { AudioWaveformVisualization } from '../shared/AudioWaveformVisualization'
import { getWaveformPreviewAction } from './soundExpertUtils'
import { sanitizeNodeError } from '../../../../utils/sanitizeNodeError'

export type { SoundExpertNodeData }

// ─── Debug ─────────────────────────────────────────────────────────────────

const log = createDebug('dw:sound-expert:mode-switch')

// ─── Constants ──────────────────────────────────────────────────────────────

const NODE_WIDTH = 315

/** Waveform rendering constants */
const BAR_COUNT = 40
const WAVEFORM_HEIGHT = 100

/** Mode display labels */
const MODE_LABELS: Record<string, string> = {
  music: 'Music',
  speech: 'Speech',
  sfx: 'SFX',
}

const MODE_ICONS: Record<string, string> = {
  music: '\u{1F3B5}',
  speech: '\u{1F399}\uFE0F',
  sfx: '\u{1F4A5}',
}

/** Tabs for UnifiedBottomPanel */
const SOUND_EXPERT_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
]

// ─── Audio History Entry ────────────────────────────────────────────────────

interface AudioHistoryEntry {
  user_prompt?: string
  result?: {
    local_path?: string
    metadata?: object
  }
  metadata?: { mode?: string; duration_seconds?: number }
}

// ─── Speech Segment ─────────────────────────────────────────────────────────

interface SpeechSegment {
  character: string
  emotion: string
  timeRange: string
  audioUrl: string | null
  versions: number
  selectedVersion: number
}

// ─── Main Component ─────────────────────────────────────────────────────────

const SoundExpertNode = memo(function SoundExpertNode(props: NodeProps<SoundExpertNodeData>) {
  const { id, data, selected, dragging } = props
  const {
    cacheKey,
    audio_file,
    in_generated_audio,
    in_name = '',
    in_prompt = '',
    in_mode = 'music',
    in_duration = '30s',
    in_model = 'elevenlabs_v3',
    in_loop = 'off',
    in_lyrics_mode = 'auto',
    in_voice_profile,
    in_text = '',
    status = 'idle',
    progressMessage,
    error,
  } = data
  const projectId = useProjectId()
  const showInsufficientSparks = useShowInsufficientSparks()
  // Per-node 1s cooldown so a fast double-tap on Generate can't fire
  // /execute-node twice.
  const { run: runGenerate } = useClickLock(1000)

  const { setNodes, getNodes, getEdges } = useReactFlow<ReactFlowNodeData, ReactFlowCanvasEdgeData>()
  const canvas = useCanvasOptional()
  const {
    handleDelete,
    handleDuplicate,
    handleDownload,
    handleAddToChat,
    handleSaveToLibrary,
  } = useNodeActions({ nodeId: id })

  // ── Per-mode prompt state (preserved across mode switches) ──────────────
  const [musicPrompt, setMusicPrompt] = useState(in_mode === 'music' ? in_prompt : '')
  const [sfxPrompt, setSfxPrompt] = useState(in_mode === 'sfx' ? in_prompt : '')
  const [speechContent, setSpeechContent] = useState(in_text)
  const [retryCount, setRetryCount] = useState(1)

  // ── Bottom panel state ──────────────────────────────────────────────────
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('config')

  // ── Audio URL resolution ──────────────────────────────────────────────────

  let audioPath = audio_file
  if (!audioPath && in_generated_audio?.history?.length) {
    const idx = in_generated_audio.picked_index ?? 0
    audioPath = in_generated_audio.history[idx]?.result?.local_path
  }
  const audioUrl =
    audioPath && projectId ? getAssetUrl(projectId, audioPath, 0, cacheKey) : null

  const isRunning = status === 'running'
  const isError = status === 'error'
  const hasAudio = !!audioUrl
  const mode = in_mode

  // ── Stale-run detection ───────────────────────────────────────────────────

  useEffect(() => {
    if (status === 'running') {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'idle', error: 'Generation interrupted' } }
            : n,
        ),
      )
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-open bottom panel ────────────────────────────────────────────────

  const batchPending = isRunning ? (data.batch_pending ?? 0) : 0

  useBottomPanelAutoOpen({
    selected,
    isRunning,
    batchPending,
    setBottomPanelOpen,
    setActiveTab,
    defaultTab: 'config',
  })

  // ── History ───────────────────────────────────────────────────────────────

  const historyEntries = useMemo(
    (): AudioHistoryEntry[] => in_generated_audio?.history ?? [],
    [in_generated_audio?.history],
  )
  const pickedIndex = in_generated_audio?.picked_index ?? 0

  const handleHistorySelect = useCallback(
    (index: number) => {
      const entry = historyEntries[index]
      const localPath = entry?.result?.local_path
      if (!localPath) return

      const currentNode = getNodes().find((n) => n.id === id)
      const currentGenAudio = currentNode
        ? getTypedNodeData(currentNode, 'soundExpert')?.in_generated_audio ?? in_generated_audio
        : in_generated_audio
      const newCacheKey = String(Date.now())
      const updatedGenAudio = { ...(currentGenAudio ?? {}), picked_index: index }

      setNodes((nds) =>
        nds.map((n) =>
          n.id !== id
            ? n
            : {
                ...n,
                data: {
                  ...n.data,
                  in_generated_audio: updatedGenAudio,
                  audio_file: localPath,
                  cacheKey: newCacheKey,
                },
              },
        ),
      )
      canvas?.updateNodeData(id, {
        in_generated_audio: updatedGenAudio,
        audio_file: localPath,
        cacheKey: newCacheKey,
      })
    },
    [id, historyEntries, getNodes, setNodes, canvas, in_generated_audio],
  )

  // ── Config updates ────────────────────────────────────────────────────────

  const updateConfig = useCallback(
    (key: string, value: string | undefined) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [key]: value } } : n)),
      )
      canvas?.updateNodeData(id, { [key]: value })
    },
    [id, setNodes, canvas],
  )

  // Batch multiple field updates into a single YDoc write to avoid
  // observer-echo race conditions (e.g. mode + prompt in one transaction).
  const updateConfigBatch = useCallback(
    (updates: Record<string, string | undefined>) => {
      log('updateConfigBatch: fields=%o', Object.keys(updates))
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...updates } } : n)),
      )
      canvas?.updateNodeData(id, updates)
    },
    [id, setNodes, canvas],
  )

  const handleRename = useCallback(
    (newName: string) => {
      updateConfig('in_name', newName)
    },
    [updateConfig],
  )

  const handleModeChange = useCallback(
    (newMode: SoundMode) => {
      log('handleModeChange: %s -> %s', mode, newMode)
      // Save current prompt to per-mode state
      if (mode === 'music') setMusicPrompt(in_prompt)
      else if (mode === 'sfx') setSfxPrompt(in_prompt)

      // Restore the target mode's prompt
      const restoredPrompt =
        newMode === 'music' ? musicPrompt : newMode === 'sfx' ? sfxPrompt : ''

      // Batch mode + prompt into a single YDoc write to prevent the observer
      // from echoing back stale field values between two separate writes.
      if (newMode !== 'speech') {
        log('handleModeChange: batching in_mode=%s + in_prompt (len=%d)', newMode, restoredPrompt.length)
        updateConfigBatch({ in_mode: newMode, in_prompt: restoredPrompt })
      } else {
        updateConfig('in_mode', newMode)
      }
    },
    [mode, in_prompt, musicPrompt, sfxPrompt, updateConfig, updateConfigBatch],
  )

  const handleMusicPromptChange = useCallback(
    (value: string) => {
      setMusicPrompt(value)
      updateConfig('in_prompt', value)
    },
    [updateConfig],
  )

  const handleSfxPromptChange = useCallback(
    (value: string) => {
      setSfxPrompt(value)
      updateConfig('in_prompt', value)
    },
    [updateConfig],
  )

  const handleSpeechContentChange = useCallback(
    (state: RichTextBoxState) => {
      setSpeechContent(state.plainText)
      updateConfig('in_text', state.plainText)
    },
    [updateConfig],
  )

  const handleDurationChange = useCallback(
    (value: string) => updateConfig('in_duration', value),
    [updateConfig],
  )

  const handleLoopChange = useCallback(
    (value: string) => updateConfig('in_loop', value),
    [updateConfig],
  )

  const handleLyricsModeChange = useCallback(
    (value: string) => updateConfig('in_lyrics_mode', value),
    [updateConfig],
  )

  const handleModelChange = useCallback(
    (value: string) => updateConfig('in_model', value),
    [updateConfig],
  )

  // ── Validation ────────────────────────────────────────────────────────────

  const getValidationError = useCallback((): string | null => {
    if (mode === 'music' && !musicPrompt.trim())
      return 'A prompt is required to generate music.'
    if (mode === 'sfx' && !sfxPrompt.trim())
      return 'A prompt is required to generate a sound effect.'
    if (mode === 'speech' && !speechContent.trim())
      return 'Script text is required for speech generation.'
    if (!projectId) return 'No project selected.'
    return null
  }, [mode, musicPrompt, sfxPrompt, speechContent, projectId])

  // ── Generate handler ──────────────────────────────────────────────────────

  const handleGenerate = useCallback(async () => {
    if (status === 'running') return

    const validationError = getValidationError()
    if (validationError) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: validationError } }
            : n,
        ),
      )
      return
    }

    void runGenerate(id, async () => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'running', progress: 0, error: undefined } }
            : n,
        ),
      )

      try {
        const pid = projectId
        if (!pid) return

        await executeNode(pid, id, undefined, retryCount)
      } catch (err) {
        if (err instanceof InsufficientSparksError) {
          showInsufficientSparks({ balance: err.balance, required: err.required, nodeId: id })
          setNodes((nds) =>
            nds.map((n) =>
              n.id === id
                ? { ...n, data: { ...n.data, status: 'idle', error: undefined } }
                : n,
            ),
          )
          return
        }
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
              : n,
          ),
        )
      }
    })
  }, [
    projectId,
    id,
    status,
    setNodes,
    retryCount,
    getValidationError,
    runGenerate,
    showInsufficientSparks,
  ])

  // ── Download handler ──────────────────────────────────────────────────────

  const onDownload = useCallback(() => {
    if (!audioPath) return
    const { filename } = buildAssetDownloadFilename({
      canvasNodes: getNodes(),
      canvasEdges: getEdges(),
      nodeId: id,
      assetPath: audioPath,
      mediaType: 'audio',
      variantHistory: historyEntries,
      pickedIndex,
    })
    handleDownload(audioPath, filename)
  }, [audioPath, handleDownload, getNodes, getEdges, id, historyEntries, pickedIndex])

  // ── Canvas nodes for @-mentions ───────────────────────────────────────────

  const canvasNodes = useMemo(() => {
    try {
      return getNodes()
    } catch {
      return []
    }
  }, [getNodes])

  // ── Open panel to specific tab ───────────────────────────────────────────

  const openPanelToTab = useCallback(
    (tab: string) => {
      setActiveTab(tab)
      setBottomPanelOpen(true)
      setNodes((nodes) => nodes.map((n) => ({ ...n, selected: n.id === id })))
    },
    [id, setActiveTab, setBottomPanelOpen, setNodes],
  )

  const handleWaveformPreviewClickCapture = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (getWaveformPreviewAction({ hasAudio, selected: !!selected }) !== 'open_config') {
        return
      }
      e.stopPropagation()
      openPanelToTab('config')
    },
    [hasAudio, selected, openPanelToTab],
  )

  // ── Toolbar actions ───────────────────────────────────────────────────────

  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'download',
        label: 'Download',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        ),
        onClick: onDownload,
        hidden: !hasAudio,
      },
      {
        key: 'addToChat',
        label: 'Add to Chat',
        icon: <MessageSquarePlus size={14} />,
        onClick: handleAddToChat,
      },
      {
        key: 'save-to-library',
        label: 'Save to Library',
        icon: <BookmarkPlus className="w-4 h-4" />,
        onClick: handleSaveToLibrary,
        group: true,
      },
      {
        key: 'duplicate',
        label: 'Duplicate',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
        onClick: handleDuplicate,
        group: true,
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [
      hasAudio,
      onDownload,
      handleDuplicate,
      handleDelete,
      handleAddToChat,
      handleSaveToLibrary,
    ],
  )

  // ── Variants data for bottom panel ────────────────────────────────────────

  const variants: AudioVariant[] = useMemo(
    () =>
      historyEntries.map((entry, i) => {
        const path = entry?.result?.local_path
        return {
          index: i,
          audioUrl: path && projectId ? getAssetUrl(projectId, path, 0, cacheKey) : null,
          prompt: entry?.user_prompt || '',
          mode: entry?.metadata?.mode || 'music',
          durationSeconds: entry?.metadata?.duration_seconds,
        }
      }),
    [historyEntries, projectId, cacheKey],
  )

  // ── Panel visibility ────────────────────────────────────────────────────

  const panelVisible =
    !!selected && !dragging && bottomPanelOpen 

  // ── Render: Waveform area ─────────────────────────────────────────────────

  const renderWaveformArea = () => {
    if (isRunning) {
      return (
        <div
          className="w-full flex flex-col items-center justify-center"
          style={{ height: WAVEFORM_HEIGHT + 40 }}
        >
          <div
            className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: SOUND_ACCENT.base, borderTopColor: 'transparent' }}
          />
          <span className="text-xxs text-foreground/50 mt-2 text-center leading-relaxed">
            {progressMessage || 'Generating audio...'}
          </span>
        </div>
      )
    }

    if (hasAudio && audioUrl) {
      return (
        <div
          className="px-3 py-3"
          data-testid="waveform-area"
          onClickCapture={handleWaveformPreviewClickCapture}
        >
          <AudioWaveformVisualization
            url={audioUrl}
            height={WAVEFORM_HEIGHT}
            accentColor={SOUND_ACCENT.base}
          />
        </div>
      )
    }

    // Empty state — microphone icon + "No audio"
    return (
      <div
        className="w-full px-3 py-4 flex flex-col items-center justify-center"
        style={{ height: WAVEFORM_HEIGHT + 40 }}
        data-testid="empty-waveform"
        onClick={(e) => {
          e.stopPropagation()
          openPanelToTab('config')
        }}
      >
        <svg
          className="w-6 h-6 text-foreground/15 mt-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
          />
        </svg>
        <span className="text-xxs text-foreground/20 mt-1">No audio</span>
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="relative" data-testid="sound-expert-node">
      <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

      {/* Floating label above the card */}
      <FloatingNodeLabel
        name={in_name}
        fallback="Sound Expert"
        onRename={handleRename}
      />

      <div
        className={`rounded-lg overflow-hidden transition-all duration-150 ${
          isRunning ? 'border-2 animate-border-glow' : ''
        }`}
        style={{
          width: NODE_WIDTH,
          boxShadow: selected
            ? '0 0 0 2px rgba(255,255,255,0.7)'
            : isError
              ? '0 0 0 1px rgba(239,68,68,0.5)'
              : isRunning
                ? '0 0 0 1px rgba(6,182,212,0.5)'
                : '0 2px 8px rgba(0,0,0,0.3)',
        }}
      >
        {/* Connection handles (transparent, VideoGeneratorNode pattern) */}
        <Handle
          type="target"
          position={Position.Top}
          id="in_upstream"
          className="!bg-transparent !border-0 !w-4 !h-1"
        />
        <Handle
          type="target"
          position={Position.Left}
          id="in_voice_profile"
          className="!bg-transparent !border-0 !w-1 !h-4"
          style={{ top: '30%' }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="in_text"
          className="!bg-transparent !border-0 !w-1 !h-4"
          style={{ top: '70%' }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="out_audio"
          className="!bg-transparent !border-0 !w-4 !h-1"
        />
        <Handle
          type="source"
          position={Position.Right}
          id="out_duration_seconds"
          className="!bg-transparent !border-0 !w-1 !h-4"
          style={{ top: '50%' }}
        />

        {/* Waveform display area */}
        <div className="bg-canvas">
          {renderWaveformArea()}

          {/* Error display */}
          {isError && error && (
            <div className="px-3 pb-2">
              <div className="text-xxs text-destructive bg-destructive/10 rounded px-2 py-1.5" title={error}>
                {sanitizeNodeError(error)}
              </div>
            </div>
          )}
        </div>

        {/* Quick Info Bar — 2 columns: Config | Variants */}
        {!isRunning && (
          <div
            data-testid="quick-info-bar"
            className="grid grid-cols-2 border-t border-border-subtle bg-surface-0"
          >
            {/* Config Column */}
            <button
              data-testid="quick-info-config"
              onClick={(e) => {
                e.stopPropagation()
                openPanelToTab('config')
              }}
              className="py-2.5 px-3 flex flex-col items-start gap-1 hover:bg-surface-1 transition-colors nodrag border-r border-border-subtle"
            >
              <span className="text-xxs text-muted-foreground leading-none">Config</span>
              <span className="text-xs text-muted-foreground truncate flex items-center gap-1">
                <span>{MODE_ICONS[mode] || ''}</span>
                {MODE_LABELS[mode] || mode}
              </span>
            </button>

            {/* Variants Column */}
            <button
              data-testid="quick-info-variants"
              onClick={(e) => {
                e.stopPropagation()
                openPanelToTab('variants')
              }}
              className="py-2.5 px-3 flex flex-col items-start gap-1 hover:bg-surface-1 transition-colors nodrag"
            >
              <span className="text-xxs text-muted-foreground leading-none">Variants</span>
              <div className="flex items-center gap-1.5">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor:
                      historyEntries.length > 0 ? SOUND_ACCENT.base : 'var(--text-muted)',
                  }}
                />
                <span className="text-xs font-medium text-foreground">
                  {historyEntries.length}
                </span>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* UnifiedBottomPanel */}
      <UnifiedBottomPanel
        tabs={SOUND_EXPERT_TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isVisible={panelVisible}
        minWidth={500}
        contentHeight={340}
      >
        {(tab) =>
          tab === 'config' ? (
            <SoundExpertConfigTab
              mode={mode}
              musicPrompt={musicPrompt}
              sfxPrompt={sfxPrompt}
              speechContent={speechContent}
              duration={in_duration}
              loop={in_loop}
              lyricsMode={in_lyrics_mode}
              model={in_model}
              voiceProfile={in_voice_profile}
              isRunning={isRunning}
              retryCount={retryCount}
              canvasNodes={canvasNodes}
              onModeChange={handleModeChange}
              onMusicPromptChange={handleMusicPromptChange}
              onSfxPromptChange={handleSfxPromptChange}
              onSpeechContentChange={handleSpeechContentChange}
              onDurationChange={handleDurationChange}
              onLoopChange={handleLoopChange}
              onLyricsModeChange={handleLyricsModeChange}
              onModelChange={handleModelChange}
              onRetryCountChange={setRetryCount}
              onGenerate={handleGenerate}
            />
          ) : (
            <AudioVariantsTabContent
              variants={variants}
              selectedIndex={pickedIndex}
              batchPending={batchPending}
              onSelect={handleHistorySelect}
            />
          )
        }
      </UnifiedBottomPanel>
    </div>
  )
})

SoundExpertNode.displayName = 'SoundExpertNode'

export default SoundExpertNode
export { SoundExpertNode, NODE_WIDTH, BAR_COUNT, WAVEFORM_HEIGHT, SOUND_EXPERT_TABS }
export type { SoundMode, SpeechSegment }
